import Button from '@jetbrains/ring-ui/components/button/button'
import {ControlsHeight} from '@jetbrains/ring-ui/components/global/controls-height'
import Link from '@jetbrains/ring-ui/components/link/link'
import {skipToken} from '@reduxjs/toolkit/query'
import classNames from 'classnames'
import {EntryPointContainer} from 'react-relay'
import type {PreloadedEntryPoint} from 'react-relay/relay-hooks/EntryPointTypes'

import {EntityProvider} from '../../../contexts/entity'
import {usePermission} from '../../../hooks/usePermission'
import SvgIcon from '../../../library/components/SvgIcon/SvgIcon'
import {sidebarArg} from '../../../rest/projects'
import {createRootSubprojectHref} from '../../../routes'
import {restApi} from '../../../services/rest'
import {Permission, ROOT_PROJECT_ID} from '../../../types'
import {internalProps} from '../../../types/BS_types'
import version from '../../../utils/version'
import {feedbackHref, whatsNewHref} from '../../App/Header/HeaderTrayHelp/HeaderTrayHelp.consts'
import Loader from '../../common/Loader/Loader'
import UIPlaceholder from '../../common/UIPlaceholder/UIPlaceholder'

import type {Props} from './FavoriteProjectsPage.types'

import styles from './FavoriteProjectsPage.css'

const leftTipText =
  'Choose and create projects or\u00A0build\u00A0configurations. For\u00A0quick access add them to your favorites.'

function FavoriteProjectsPage({
  hasProjects,
  hasFavoriteProjects,
  projectsLoaded,
  canShowAllProjects,
  entryPoint,
}: Props) {
  const hasPermissionToCreate = usePermission(Permission.EDIT_PROJECT, ROOT_PROJECT_ID)
  restApi.endpoints.getAllProjectsNormalized.useQuerySubscription(
    hasFavoriteProjects || projectsLoaded ? skipToken : sidebarArg,
  )

  if (hasFavoriteProjects == null || (!hasFavoriteProjects && !projectsLoaded)) {
    return <Loader />
  } else if (hasFavoriteProjects || (hasProjects && canShowAllProjects)) {
    return (
      <EntityProvider projectId={ROOT_PROJECT_ID}>
        <EntryPointContainer
          entryPointReference={entryPoint as PreloadedEntryPoint<any>}
          props={{}}
        />
      </EntityProvider>
    )
  }

  const uiProp = internalProps['teamcity.ui.experimental']
  const isDefaultUI = ['enableDefault', 'enableAll', 'enableAllSoft'].includes(uiProp)
  const customTitle = internalProps['teamcity.ui.customWelcomeTitle']
  const title =
    customTitle != null && customTitle.length > 0
      ? customTitle
      : isDefaultUI
        ? 'Welcome to TeamCity'
        : 'Welcome to TeamCity Sakura UI'

  return (
    <div className={styles.overviewPlaceholder}>
      {hasProjects && (
        <div className={classNames(styles.tip, styles.tipLeft)}>
          <SvgIcon icon="arrow-line-left" className={styles.arrow} />
          {leftTipText}
        </div>
      )}
      <UIPlaceholder title={title}>
        <div className={styles.linksContainer}>
          {hasPermissionToCreate && (
            <Button
              href={createRootSubprojectHref}
              className={styles.button}
              rel="noreferrer"
              data-test="create-project"
              primary
              height={ControlsHeight.M}
            >
              {'Create project...'}
            </Button>
          )}
          <Link href={whatsNewHref} className={styles.link} target="_blank" rel="noreferrer">
            {`What's new in ${version.full}`}
          </Link>
          <Link href={feedbackHref} className={styles.link} target="_blank" rel="noreferrer">
            {'Send feedback...'}
          </Link>
        </div>
      </UIPlaceholder>
    </div>
  )
}

export default FavoriteProjectsPage
